/**
 * Client-side highlight + lint for MY/SCORM custom CSS.
 * Allowlist must stay in sync with awarego-api/src/utils/sanitizePlayerCss.js
 * (unknown properties and disallowed constructs are stripped on save).
 */

const ALLOWED_AT_RULES = new Set([
    '@media',
    '@supports',
    '@font-face',
    '@keyframes',
    '@-webkit-keyframes',
]);

const ALLOWED_PROPERTIES = new Set([
    'accent-color',
    'align-content',
    'align-items',
    'align-self',
    'aspect-ratio',
    'background',
    'background-attachment',
    'background-clip',
    'background-color',
    'background-image',
    'background-origin',
    'background-position',
    'background-repeat',
    'background-size',
    'border',
    'border-bottom',
    'border-bottom-color',
    'border-bottom-left-radius',
    'border-bottom-right-radius',
    'border-bottom-style',
    'border-bottom-width',
    'border-collapse',
    'border-color',
    'border-left',
    'border-left-color',
    'border-left-style',
    'border-left-width',
    'border-radius',
    'border-right',
    'border-right-color',
    'border-right-style',
    'border-right-width',
    'border-spacing',
    'border-style',
    'border-top',
    'border-top-color',
    'border-top-left-radius',
    'border-top-right-radius',
    'border-top-style',
    'border-top-width',
    'border-width',
    'bottom',
    'box-shadow',
    'box-sizing',
    'caret-color',
    'color',
    'column-gap',
    'cursor',
    'direction',
    'display',
    'fill',
    'filter',
    'flex',
    'flex-basis',
    'flex-direction',
    'flex-grow',
    'flex-shrink',
    'flex-wrap',
    'font',
    'font-display',
    'font-family',
    'font-feature-settings',
    'font-kerning',
    'font-size',
    'font-size-adjust',
    'font-stretch',
    'font-style',
    'font-variant',
    'font-variation-settings',
    'font-weight',
    'gap',
    'grid',
    'grid-area',
    'grid-auto-columns',
    'grid-auto-flow',
    'grid-auto-rows',
    'grid-column',
    'grid-row',
    'grid-template',
    'grid-template-areas',
    'grid-template-columns',
    'grid-template-rows',
    'height',
    'hyphens',
    'justify-content',
    'justify-items',
    'justify-self',
    'left',
    'letter-spacing',
    'line-height',
    'list-style',
    'list-style-position',
    'list-style-type',
    'margin',
    'margin-bottom',
    'margin-left',
    'margin-right',
    'margin-top',
    'max-height',
    'max-width',
    'min-height',
    'min-width',
    'object-fit',
    'object-position',
    'opacity',
    'order',
    'outline',
    'outline-color',
    'outline-offset',
    'outline-style',
    'outline-width',
    'overflow',
    'overflow-wrap',
    'overflow-x',
    'overflow-y',
    'padding',
    'padding-bottom',
    'padding-left',
    'padding-right',
    'padding-top',
    'place-content',
    'place-items',
    'place-self',
    'pointer-events',
    'position',
    'right',
    'row-gap',
    'src',
    'stroke',
    'stroke-width',
    'table-layout',
    'text-align',
    'text-decoration',
    'text-decoration-color',
    'text-decoration-line',
    'text-decoration-style',
    'text-indent',
    'text-overflow',
    'text-shadow',
    'text-transform',
    'top',
    'transform',
    'transform-origin',
    'transition',
    'transition-delay',
    'transition-duration',
    'transition-property',
    'transition-timing-function',
    'unicode-bidi',
    'unicode-range',
    'user-select',
    'vertical-align',
    'visibility',
    'white-space',
    'width',
    'word-break',
    'word-spacing',
    'z-index',
]);

export function isAllowedProperty(name) {
    const prop = String(name || '')
        .trim()
        .toLowerCase();
    if (!prop) return false;
    if (prop.startsWith('--ag-')) return true;
    return ALLOWED_PROPERTIES.has(prop);
}

function lineAt(css, index) {
    let line = 1;
    const end = Math.min(index, css.length);
    for (let i = 0; i < end; i++) {
        if (css[i] === '\n') line += 1;
    }
    return line;
}

function escapeHtml(text) {
    return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

function span(cls, text) {
    return `<span class="${cls}">${escapeHtml(text)}</span>`;
}

function readString(css, start) {
    const quote = css[start];
    let i = start + 1;
    while (i < css.length) {
        if (css[i] === '\\') {
            i += 2;
            continue;
        }
        if (css[i] === quote) return { end: i + 1, closed: true };
        i += 1;
    }
    return { end: css.length, closed: false };
}

function readComment(css, start) {
    const end = css.indexOf('*/', start + 2);
    if (end < 0) return { end: css.length, closed: false };
    return { end: end + 2, closed: true };
}

function skipWsAndComments(css, i) {
    const n = css.length;
    while (i < n) {
        if (/\s/.test(css[i])) {
            i += 1;
            continue;
        }
        if (css[i] === '/' && css[i + 1] === '*') {
            const c = readComment(css, i);
            i = c.end;
            if (!c.closed) break;
            continue;
        }
        break;
    }
    return i;
}

/**
 * Highlight CSS for the overlay editor. Unknown/disallowed properties
 * get .pce-property-bad so they show as dropped-on-save.
 */
export function highlightCss(css) {
    if (!css) return '';
    let i = 0;
    const n = css.length;
    let out = '';
    // 'sheet' | 'block' (nested @media) | 'decls'
    const stack = ['sheet'];

    const context = () => stack[stack.length - 1] || 'sheet';

    while (i < n) {
        if (css[i] === '/' && css[i + 1] === '*') {
            const c = readComment(css, i);
            out += span('pce-comment', css.slice(i, c.end));
            i = c.end;
            continue;
        }
        if (css[i] === '"' || css[i] === "'") {
            const s = readString(css, i);
            out += span('pce-string', css.slice(i, s.end));
            i = s.end;
            continue;
        }
        if (/\s/.test(css[i])) {
            const start = i;
            while (i < n && /\s/.test(css[i])) i += 1;
            out += escapeHtml(css.slice(start, i));
            continue;
        }
        if (css[i] === '{') {
            const prev = context();
            out += span('pce-punct', '{');
            i += 1;
            if (prev === 'pending-at-block') {
                stack[stack.length - 1] = 'block';
            } else if (prev === 'pending-at-decls') {
                stack[stack.length - 1] = 'decls';
            } else {
                stack.push('decls');
            }
            continue;
        }
        if (css[i] === '}') {
            out += span('pce-punct', '}');
            i += 1;
            if (stack.length > 1) stack.pop();
            continue;
        }
        if (css[i] === '@') {
            const start = i;
            i += 1;
            while (i < n && /[a-zA-Z0-9_-]/.test(css[i])) i += 1;
            const name = css.slice(start, i).toLowerCase();
            out += span(
                ALLOWED_AT_RULES.has(name) || name === '@media'
                    ? 'pce-at'
                    : 'pce-at-bad',
                css.slice(start, i)
            );
            if (name === '@media' || name === '@supports') {
                stack.push('pending-at-block');
            } else if (
                name === '@font-face' ||
                name === '@keyframes' ||
                name === '@-webkit-keyframes'
            ) {
                stack.push('pending-at-decls');
            } else {
                stack.push('pending-at-block');
            }
            continue;
        }

        const ctx = context();
        if (ctx === 'decls') {
            if (css[i] === ';') {
                out += span('pce-punct', ';');
                i += 1;
                continue;
            }
            const start = i;
            while (i < n && css[i] !== ':' && css[i] !== ';' && css[i] !== '}' && css[i] !== '{') {
                if (css[i] === '/' && css[i + 1] === '*') break;
                if (css[i] === '"' || css[i] === "'") break;
                i += 1;
            }
            if (i === start) {
                out += escapeHtml(css[i]);
                i += 1;
                continue;
            }
            const prop = css.slice(start, i).trim();
            const raw = css.slice(start, i);
            if (prop) {
                const cls = isAllowedProperty(prop)
                    ? prop.startsWith('--')
                        ? 'pce-variable'
                        : 'pce-property'
                    : 'pce-property-bad';
                out += span(cls, raw);
            } else {
                out += escapeHtml(raw);
            }
            if (i < n && css[i] === ':') {
                out += span('pce-punct', ':');
                i += 1;
                const vStart = i;
                let depth = 0;
                while (i < n) {
                    if (css[i] === '/' && css[i + 1] === '*') break;
                    if (css[i] === '"' || css[i] === "'") {
                        out += span('pce-value', css.slice(vStart, i));
                        const s = readString(css, i);
                        out += span('pce-string', css.slice(i, s.end));
                        i = s.end;
                        break;
                    }
                    if (css[i] === '(') depth += 1;
                    if (css[i] === ')') depth = Math.max(0, depth - 1);
                    if ((css[i] === ';' || css[i] === '}') && depth === 0) break;
                    i += 1;
                }
                if (i > vStart && css[vStart] !== '"' && css[vStart] !== "'") {
                    out += span('pce-value', css.slice(vStart, i));
                }
            }
            continue;
        }

        // selector / at-rule prelude
        const start = i;
        while (
            i < n &&
            css[i] !== '{' &&
            css[i] !== '}' &&
            !(css[i] === '/' && css[i + 1] === '*') &&
            css[i] !== '"' &&
            css[i] !== "'" &&
            css[i] !== '@'
        ) {
            i += 1;
        }
        if (i === start) {
            out += escapeHtml(css[i]);
            i += 1;
            continue;
        }
        out += span('pce-selector', css.slice(start, i));
    }

    return out;
}

function isSafeUrl(raw) {
    const url = String(raw || '').trim();
    if (!url) return false;
    if (/^(data:image\/(png|jpeg|gif|webp|svg\+xml);base64,)/i.test(url)) {
        return true;
    }
    try {
        return new URL(url).protocol === 'https:';
    } catch (_) {
        return false;
    }
}

function addIssue(issues, seen, severity, message, line) {
    const key = `${severity}:${line || 0}:${message}`;
    if (seen.has(key)) return;
    seen.add(key);
    issues.push({ severity, message, line });
}

/**
 * Lightweight validation aligned with server sanitizer.
 * Errors = syntax / security. Warnings = will be stripped on save.
 */
export function lintPlayerCss(css) {
    const issues = [];
    const seen = new Set();
    if (!css || !String(css).trim()) return issues;
    const source = String(css);

    let i = 0;
    const n = source.length;
    let depth = 0;
    let inStr = null;

    while (i < n) {
        if (inStr) {
            if (source[i] === '\\') {
                i += 2;
                continue;
            }
            if (source[i] === inStr) inStr = null;
            i += 1;
            continue;
        }
        if (source[i] === '/' && source[i + 1] === '*') {
            const c = readComment(source, i);
            if (!c.closed) {
                addIssue(issues, seen, 'error', 'Unclosed comment', lineAt(source, i));
            }
            i = c.end;
            continue;
        }
        if (source[i] === '"' || source[i] === "'") {
            const s = readString(source, i);
            if (!s.closed) {
                addIssue(
                    issues,
                    seen,
                    'error',
                    'Unclosed string',
                    lineAt(source, i)
                );
            }
            inStr = s.closed ? null : source[i];
            i = s.end;
            continue;
        }
        if (source[i] === '{') depth += 1;
        if (source[i] === '}') {
            depth -= 1;
            if (depth < 0) {
                addIssue(
                    issues,
                    seen,
                    'error',
                    'Extra closing brace }',
                    lineAt(source, i)
                );
                depth = 0;
            }
        }
        i += 1;
    }
    if (depth > 0) {
        addIssue(issues, seen, 'error', 'Unclosed opening brace {', 1);
    }

    if (/<\/?style/i.test(source) || /<script/i.test(source)) {
        addIssue(
            issues,
            seen,
            'error',
            'HTML is not allowed in custom CSS',
            1
        );
    }
    if (/@import\b/i.test(source)) {
        addIssue(
            issues,
            seen,
            'warning',
            '@import is not supported. Add a custom font URL above instead',
            lineAt(source, source.search(/@import\b/i))
        );
    }
    if (/expression\s*\(/i.test(source)) {
        addIssue(issues, seen, 'error', 'expression() is not supported', 1);
    }
    if (/javascript\s*:/i.test(source) || /vbscript\s*:/i.test(source)) {
        addIssue(issues, seen, 'error', 'javascript: / vbscript: URLs are not supported', 1);
    }
    if (/-moz-binding/i.test(source) || /(^|[\s;])behavior\s*:/i.test(source)) {
        addIssue(issues, seen, 'error', 'behavior / -moz-binding is not supported', 1);
    }

    const atRuleRe = /@[a-z-]+/gi;
    let atMatch;
    while ((atMatch = atRuleRe.exec(source))) {
        const name = atMatch[0].toLowerCase();
        if (name === '@import') continue;
        if (!ALLOWED_AT_RULES.has(name)) {
            addIssue(
                issues,
                seen,
                'warning',
                `${name} is not supported and will be ignored`,
                lineAt(source, atMatch.index)
            );
        }
    }

    walkRules(source, 0, source, issues, seen);

    const urlRe = /url\s*\(\s*(['"]?)([^)'"]+)\1\s*\)/gi;
    let urlMatch;
    while ((urlMatch = urlRe.exec(source))) {
        if (!isSafeUrl(urlMatch[2])) {
            addIssue(
                issues,
                seen,
                'warning',
                `Only https or image data URLs are supported (${urlMatch[2].slice(0, 48)})`,
                lineAt(source, urlMatch.index)
            );
        }
    }

    return issues.sort((a, b) => (a.line || 0) - (b.line || 0));
}

function nextBlock(css, from) {
    let i = skipWsAndComments(css, from);
    if (i >= css.length || css[i] === '}') return null;
    const start = i;
    let brace = -1;
    while (i < css.length) {
        if (css[i] === '/' && css[i + 1] === '*') {
            i = readComment(css, i).end;
            continue;
        }
        if (css[i] === '"' || css[i] === "'") {
            i = readString(css, i).end;
            continue;
        }
        if (css[i] === '{') {
            brace = i;
            break;
        }
        if (css[i] === '}') break;
        i += 1;
    }
    if (brace < 0) return null;
    i = brace + 1;
    let depth = 1;
    while (i < css.length && depth > 0) {
        if (css[i] === '/' && css[i + 1] === '*') {
            i = readComment(css, i).end;
            continue;
        }
        if (css[i] === '"' || css[i] === "'") {
            i = readString(css, i).end;
            continue;
        }
        if (css[i] === '{') depth += 1;
        if (css[i] === '}') depth -= 1;
        i += 1;
    }
    return {
        start,
        end: i,
        prelude: css.slice(start, brace).trim(),
        body: css.slice(brace + 1, i - 1),
        bodyOffset: brace + 1,
    };
}

function walkRules(css, baseOffset, source, issues, seen) {
    let i = 0;
    while (i < css.length) {
        const block = nextBlock(css, i);
        if (!block) break;
        const prelude = block.prelude;
        const absBody = baseOffset + block.bodyOffset;
        if (prelude[0] === '@') {
            const m = prelude.match(/^@[a-z-]+/i);
            const name = m ? m[0].toLowerCase() : '';
            if (
                name === '@media' ||
                name === '@supports' ||
                name === '@keyframes' ||
                name === '@-webkit-keyframes'
            ) {
                walkRules(block.body, absBody, source, issues, seen);
            } else {
                lintDeclarations(block.body, absBody, source, issues, seen);
            }
        } else {
            lintDeclarations(block.body, absBody, source, issues, seen);
        }
        i = block.end;
    }
}

function lintDeclarations(body, bodyOffset, source, issues, seen) {
    let i = 0;
    const n = body.length;
    while (i < n) {
        i = skipWsAndComments(body, i);
        if (i >= n) break;
        if (body[i] === '}') break;
        if (body[i] === '@' || body[i] === '{') return;
        const start = i;
        while (i < n && body[i] !== ':' && body[i] !== ';' && body[i] !== '{' && body[i] !== '}') {
            if (body[i] === '/' && body[i + 1] === '*') break;
            i += 1;
        }
        if (i < n && body[i] === ':') {
            const prop = body.slice(start, i).trim();
            if (prop && !isAllowedProperty(prop)) {
                const msg = prop.startsWith('--')
                    ? `${prop} is not supported (use --ag- variables)`
                    : `${prop} is not supported and will be ignored`;
                addIssue(
                    issues,
                    seen,
                    'warning',
                    msg,
                    lineAt(source, bodyOffset + start)
                );
            }
            i += 1;
            let depth = 0;
            while (i < n) {
                if (body[i] === '/' && body[i + 1] === '*') {
                    i = readComment(body, i).end;
                    continue;
                }
                if (body[i] === '"' || body[i] === "'") {
                    i = readString(body, i).end;
                    continue;
                }
                if (body[i] === '(') depth += 1;
                if (body[i] === ')') depth = Math.max(0, depth - 1);
                if ((body[i] === ';' || body[i] === '}') && depth === 0) break;
                i += 1;
            }
            if (i < n && body[i] === ';') i += 1;
            continue;
        }
        i += 1;
    }
}

export { ALLOWED_PROPERTIES, ALLOWED_AT_RULES };
